import { Category } from '@/domain/models/content/category'
import { i18nTranslationHelper } from '@/i18n'
import { settings } from '@/main/config/settings'
import { LinkConstants, LinkConstantType } from '@/main/routes/helpers/link-constants'
import PathConstants from '@/main/routes/paths'
import { ValidAccountState } from '@/presentation/contexts/auth'
import { WINDOW_REL, WINDOW_TARGET } from '@/presentation/helpers'
import useAccount from '@/presentation/hooks/auth/use-account'
import logoImage from '@images/logo-unico-skill.svg'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import {
  Box,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'

type BaseFooterProps = {
  variant?: 'compact' | 'full'
}

export default function BaseFooter({ variant = 'full' }: BaseFooterProps) {
  const theme = useTheme()
  const [account] = useAccount() as ValidAccountState
  const categories = Category.getAvailables(account.company.contentRestrictions.categories)
  const t = i18nTranslationHelper.getTComponents

  const lgScreenMatches = useMediaQuery(theme.breakpoints.down('lg'))

  const trackNavigate = (destination: string | LinkConstantType | PathConstants.RouteProps) => {
    const data = typeof destination === 'string' ? destination : destination.name
    AnalyticsTracker.trackEvent(AnalyticsEventName.FOOTER_NAVIGATE, {
      destination: data
    })
  }

  const renderLogo = () => (
    <Box
      component={Link}
      to={PathConstants.Routes.HOME.value}
      className="cursor-pointer"
      onClick={() => trackNavigate(PathConstants.Routes.HOME)}
    >
      <img src={logoImage} alt="Unico Skill" />
    </Box>
  )

  const renderLinks = () => (
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: 'grey.700' }}>
        Unico Skill
      </Typography>

      <Typography variant="body1" className="flex flex-col gap-2">
        <Box
          component={Link}
          to={PathConstants.Routes.INSTITUTIONS.value.toLowerCase()}
          onClick={() => trackNavigate(PathConstants.Routes.INSTITUTIONS)}
          className="no-underline cursor-pointer"
          sx={{
            color: 'grey.700',
            '&:hover': {
              color: 'primary.main'
            }
          }}
        >
          {t('footer.allPartners')}
        </Box>

        <Box
          component={Link}
          to={LinkConstants.PRIVACY_POLICY.url}
          target={WINDOW_TARGET.BLANK}
          rel={WINDOW_REL.NO}
          onClick={() => trackNavigate(LinkConstants.PRIVACY_POLICY)}
          className="no-underline cursor-pointer"
          sx={{
            color: 'grey.700',
            '&:hover': {
              color: 'primary.main'
            }
          }}
        >
          {t('footer.privacyPolicy')}
        </Box>

        {account.company.politicsUrl && (
          <Box
            component={Link}
            to={account.company.politicsUrl}
            target={WINDOW_TARGET.BLANK}
            rel={WINDOW_REL.NO}
            onClick={() => trackNavigate(LinkConstants.COMPANY_POLITICS)}
            className="no-underline cursor-pointer"
            sx={{
              color: 'grey.700',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {t('footer.companyPolitics', { companyName: account.company.name })}
          </Box>
        )}

        <Box
          component={Link}
          to={LinkConstants.TERMS_OF_USE.url}
          target={WINDOW_TARGET.BLANK}
          rel={WINDOW_REL.NO}
          onClick={() => trackNavigate(LinkConstants.TERMS_OF_USE)}
          className="no-underline cursor-pointer"
          sx={{
            color: 'grey.700',
            '&:hover': {
              color: 'primary.main'
            }
          }}
        >
          {t('footer.termsOfUse')}
        </Box>
      </Typography>
    </Box>
  )

  const renderCategories = () => (
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: 'grey.700' }}>
        {t('footer.categoriesTitle')}
      </Typography>

      <Typography variant="body1" className="flex flex-col gap-2">
        {categories.map(category => (
          <Box
            key={category.id}
            component={Link}
            to={PathConstants.getCategoryRoutePath(category)}
            onClick={() => trackNavigate(category.name)}
            className="no-underline cursor-pointer"
            sx={{
              color: 'grey.700',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {category.title}
          </Box>
        ))}
      </Typography>
    </Box>
  )

  const renderHelpAndSocials = () => (
    <Fragment>
      {account?.helpCenterConfig?.hasLink && (
        <Fragment>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: 'grey.700' }}>
            {t('footer.helpTitle')}
          </Typography>

          <Typography variant="body1" sx={{ mb: 6 }}>
            <Box
              component={Link}
              to={account?.helpCenterConfig?.redirectTo}
              target={WINDOW_TARGET.BLANK}
              rel={WINDOW_REL.NO}
              onClick={() => trackNavigate(LinkConstants.SUPPORT)}
              className="no-underline cursor-pointer"
              sx={{
                color: 'grey.700',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              {t('footer.helpCenter')}
            </Box>
          </Typography>
        </Fragment>
      )}

      <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: 'grey.700' }}>
        {t('footer.socialsTitle')}
      </Typography>

      <Link
        className="no-underline text-gray-700 cursor-pointer"
        to={LinkConstants.SOCIAL_MEDIA_LINKED_IN.url}
        target={WINDOW_TARGET.BLANK}
        rel={WINDOW_REL.NO}
        onClick={() => trackNavigate(LinkConstants.SOCIAL_MEDIA_LINKED_IN)}
      >
        <IconButton edge="start" aria-label={t('footer.linkedIn')}>
          <LinkedInIcon color="primary" sx={{ fontSize: 32 }} />
        </IconButton>
      </Link>
    </Fragment>
  )

  return (
    <Box
      component="footer"
      role="contentinfo"
      sx={{
        width: '100%',
        bgcolor: 'common.white',
        ...(variant === 'full' && { borderTop: '1px solid', borderColor: 'grey.50' })
      }}
    >
      {variant === 'full' && (
        <Container
          maxWidth="lg"
          disableGutters={!lgScreenMatches}
          sx={{ paddingY: theme.spacing(5) }}
        >
          <Grid container rowSpacing={3} columnSpacing={{ lg: 5, md: 3, sm: 3, xs: 3 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>{renderLogo()}</Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>{renderLinks()}</Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>{renderCategories()}</Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>{renderHelpAndSocials()}</Grid>
          </Grid>
        </Container>
      )}

      <Box>
        <Stack
          direction="row"
          justifyContent={{ xs: 'flex-start', md: 'center' }}
          alignItems="center"
          spacing={80}
          sx={{ height: 9, bgcolor: 'common.white', padding: 3 }}
        >
          <Typography variant="body1" className="text-gray-700">
            © {settings.BUILD_TIME.getFullYear()} Unico Skill
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}
